import type { RefCallback } from "react";
import type { CmsPage } from "../../../../types/cms";
import { sectionPath } from "../../pageAddress";
import {
  PAGE_SECTIONS,
  type PageSection,
  pageSectionLabel,
  sectionAllowsHome,
} from "../../pageSection";
import styles from "./PageAddressFields.module.css";

const SECTION_HINT =
  "The part of the site the page sits in. Its address starts with the section, and its parent page must be in the same section.";

type PageSectionFieldProps = {
  id: string;
  value: PageSection;
  onChange: (section: PageSection) => void;
  lockedReason?: string;
  sectionHome: boolean;
  onSectionHomeChange: (sectionHome: boolean) => void;
  existingHome: CmsPage | null;
  inputRef?: RefCallback<HTMLElement>;
};

export function PageSectionField({
  id,
  value,
  onChange,
  lockedReason,
  sectionHome,
  onSectionHomeChange,
  existingHome,
  inputRef,
}: PageSectionFieldProps) {
  const hintId = `${id}-hint`;
  const homeId = `${id}-home`;
  const homeHintId = `${homeId}-hint`;

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        Section
      </label>
      <select
        ref={inputRef}
        id={id}
        className={styles.select}
        value={value}
        onChange={event => {
          const section = PAGE_SECTIONS.find(option => option.value === event.target.value);
          if (section) {
            onChange(section.value);
          }
        }}
        aria-describedby={hintId}
        disabled={Boolean(lockedReason)}
      >
        {PAGE_SECTIONS.map(section => (
          <option key={section.value} value={section.value}>
            {section.label} (/{section.value}/…)
          </option>
        ))}
      </select>
      <span id={hintId} className={styles.hint}>
        {lockedReason ?? SECTION_HINT}
      </span>

      {sectionAllowsHome(value) ? (
        <>
          <label className={styles.checkboxLabel} htmlFor={homeId}>
            <input
              id={homeId}
              type="checkbox"
              checked={sectionHome}
              onChange={event => onSectionHomeChange(event.target.checked)}
              disabled={existingHome !== null}
              aria-describedby={homeHintId}
            />
            Section home
          </label>
          <span id={homeHintId} className={styles.hint}>
            {existingHome
              ? `${existingHome.title} is already the ${pageSectionLabel(value)} section home.`
              : `The page at ${sectionPath(value, "")}. It has no parent page or slug.`}
          </span>
        </>
      ) : null}
    </div>
  );
}
